import React, { useEffect } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import Modal from 'components/lib/Modal';
import TextArea from 'components/lib/TextArea';

export type ActionPlanEmailCampaignEditValues = {
  description: string;
  trigger: string;
  audience: string;
  messagingStrategy: string;
};

type Props = {
  open: boolean;
  values: ActionPlanEmailCampaignEditValues;
  isSaving?: boolean;
  onCancel: () => void;
  onSave: (values: ActionPlanEmailCampaignEditValues) => void | Promise<void>;
};

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-body {
      padding: 0 30px 0 30px;
    }
  }
`;

const Description = styled.div`
  margin-bottom: 20px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const Body = styled.div`
  padding: 0 0 30px;
`;

const Field = styled.div`
  margin-bottom: 10px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
  font-family: var(--font-bold);
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`;

const FooterButton = styled(Button)`
  min-width: 120px;
`;

const PrimaryTextArea = styled(TextArea).attrs({
  className: 'mini-scroll',
})`
  color: var(--text-primary);

  &&& {
    height: 72px;
    min-height: 72px;
  }

  &&&.mini-scroll::-webkit-scrollbar-thumb {
    height: 10px;
  }
`;

const ActionPlanEmailCampaignEditModal = ({ open, values, isSaving = false, onCancel, onSave }: Props) => {
  const [form] = Form.useForm<ActionPlanEmailCampaignEditValues>();

  const handleSave = async () => {
    try {
      const validatedValues = await form.validateFields();
      await onSave(validatedValues);
    } catch (error: any) {
      if (Array.isArray(error?.errorFields)) {
        return;
      }

      throw error;
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      description: values.description,
      trigger: values.trigger,
      audience: values.audience,
      messagingStrategy: values.messagingStrategy,
    });
  }, [form, open, values.audience, values.description, values.messagingStrategy, values.trigger]);

  return (
    <StyledModal
      className="compact bare-header"
      open={open}
      onCancel={onCancel}
      destroyOnClose
      title="Edit Campaign Details"
      width={600}
      footer={
        <Footer>
          <FooterButton type="primary" loading={isSaving} onClick={handleSave}>
            Save
          </FooterButton>
          <FooterButton onClick={onCancel} disabled={isSaving}>
            Cancel
          </FooterButton>
        </Footer>
      }
    >
      <Body>
        <Description>Edit description, trigger, audience and message strategy below.</Description>
        <Form form={form} layout="vertical">
          <Field>
            <Label>Description</Label>
            <Form.Item
              name="description"
              rules={[{ required: true, message: 'Description is required' }]}
              style={{ marginBottom: 0 }}
            >
              <PrimaryTextArea />
            </Form.Item>
          </Field>
          <Field>
            <Label>Trigger</Label>
            <Form.Item
              name="trigger"
              rules={[{ required: true, message: 'Trigger is required' }]}
              style={{ marginBottom: 0 }}
            >
              <PrimaryTextArea />
            </Form.Item>
          </Field>
          <Field>
            <Label>Audience</Label>
            <Form.Item
              name="audience"
              rules={[{ required: true, message: 'Audience is required' }]}
              style={{ marginBottom: 0 }}
            >
              <PrimaryTextArea />
            </Form.Item>
          </Field>
          <Field style={{ marginBottom: 0 }}>
            <Label>Messaging Strategy</Label>
            <Form.Item
              name="messagingStrategy"
              rules={[{ required: true, message: 'Messaging strategy is required' }]}
              style={{ marginBottom: 0 }}
            >
              <PrimaryTextArea />
            </Form.Item>
          </Field>
        </Form>
      </Body>
    </StyledModal>
  );
};

export default ActionPlanEmailCampaignEditModal;
